import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { clsx } from 'clsx';

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

interface LanguageSwitcherProps {
  /** 'icon' = compact globe button, 'full' = with label (for menus) */
  variant?: 'icon' | 'full';
  className?: string;
}

export default function LanguageSwitcher({ variant = 'icon', className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className={clsx('relative', className)}>
      <button
        onClick={() => setOpen(!open)}
        className={clsx(
          'flex items-center gap-2 transition-colors rounded-lg',
          variant === 'icon'
            ? 'p-2 hover:bg-gray-100 text-gray-600 hover:text-gray-900'
            : 'w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100'
        )}
        title={currentLang.label}
      >
        {variant === 'icon' ? (
          <>
            <Globe className="w-4 h-4" />
            <span className="text-xs font-medium uppercase">{currentLang.code}</span>
          </>
        ) : (
          <>
            <Globe className="w-4 h-4" />
            <span className="flex-1 text-left">{currentLang.flag} {currentLang.label}</span>
          </>
        )}
      </button>

      {open && (
        <div className={clsx(
          'absolute z-50 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1',
          variant === 'icon' ? 'right-0' : 'left-0 bottom-full mb-1'
        )}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={clsx(
                'flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors',
                lang.code === currentLang.code
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
            >
              <span>{lang.flag}</span>
              <span className="flex-1 text-left">{lang.label}</span>
              {lang.code === currentLang.code && (
                <Check className="w-4 h-4 text-primary-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
